import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../services/adminService';
import { 
  ArrowLeft, Copy, Phone, Mail, MapPin, Calendar, User, Wrench, 
  DollarSign, FileText, Image as ImageIcon, Clock, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const ComplaintDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const { data: complaint, isLoading, refetch } = useQuery({
    queryKey: ['complaintDetails', id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_get_complaint_by_id', {
        p_complaint_id: id
      });

      if (error) throw error;
      if (!data) throw new Error('Complaint not found');

      // Fetch advisor and mechanic names
      let advisorName = null;
      let mechanicName = null;
      let mechanicContact = null;

      if (data.assigned_advisor_id) {
        const { data: advisorData } = await supabase
          .from('users')
          .select('name')
          .eq('id', data.assigned_advisor_id)
          .single();
        advisorName = advisorData?.name;
      }

      if (data.assigned_mechanic_id) {
        const { data: mechanicData } = await supabase
          .from('users')
          .select('name, phone')
          .eq('id', data.assigned_mechanic_id)
          .single();
        mechanicName = mechanicData?.name;
        mechanicContact = mechanicData?.phone;
      }

      return {
        ...data,
        assigned_advisor_name: advisorName,
        assigned_mechanic_name: mechanicName,
        assigned_mechanic_contact: mechanicContact
      };
    },
    enabled: !!id
  });

  const { data: timeline } = useQuery({
    queryKey: ['complaintTimeline', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('service_request_status_history')
        .select('*')
        .eq('service_request_id', id)
        .order('changed_at', { ascending: false });

      if (error) throw error;

      // Enrich with user names
      const enriched = await Promise.all(
        (data || []).map(async (h) => {
          let userName = null;
          if (h.changed_by) {
            const { data: userData } = await supabase
              .from('users')
              .select('name')
              .eq('id', h.changed_by)
              .single();
            userName = userData?.name;
          }
          return {
            ...h,
            changed_by_name: userName
          };
        })
      );

      return enriched;
    },
    enabled: !!id
  });

  useEffect(() => {
    if (complaint) {
      setAdminNotes(complaint.admin_notes || '');
    }
  }, [complaint]);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const { error } = await supabase
        .from('service_requests')
        .update({ 
          admin_notes: adminNotes,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Add to history
      await supabase
        .from('service_request_status_history')
        .insert({
          service_request_id: id,
          status: 'ADMIN_NOTE_ADDED',
          changed_at: new Date().toISOString(),
          changed_by: 'admin' // Should be current user ID
        });

      alert('Admin notes saved successfully');
      setShowNotesModal(false);
      refetch();
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save admin notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    alert(`${label} copied to clipboard`);
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'completed') return '#10b981';
    if (s === 'pending' || s === 'unassigned') return '#f59e0b';
    if (s === 'assigned' || s.includes('progress')) return '#3b82f6';
    if (s === 'cancelled') return '#ef4444';
    return '#6b7280';
  };

  const getPriorityColor = (priority) => {
    const p = priority?.toLowerCase() || '';
    if (p === 'high') return '#ef4444';
    if (p === 'medium') return '#f59e0b';
    if (p === 'low') return '#10b981';
    return '#6b7280';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading complaint details...</p>
        </div>
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="text-center py-12">
        <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-500">Complaint not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/complaints')}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Complaint Details</h1>
            <p className="text-gray-600 mt-1">ID: {complaint.id}</p>
          </div>
        </div>
        <button
          onClick={() => setShowNotesModal(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center space-x-2"
        >
          <FileText size={20} />
          <span>Edit Notes</span>
        </button>
      </div>

      {/* Status and Priority */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-2">Status</p>
          <span
            className="px-3 py-1 text-sm font-medium rounded text-white"
            style={{ backgroundColor: getStatusColor(complaint.request_status) }}
          >
            {complaint.request_status || 'N/A'}
          </span>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-2">Priority</p>
          <span
            className="px-3 py-1 text-sm font-medium rounded text-white"
            style={{ backgroundColor: getPriorityColor(complaint.priority) }}
          >
            {complaint.priority || 'N/A'}
          </span>
        </div>
      </div>

      {/* Customer Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <User size={20} className="mr-2" />
          Customer Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Name</p>
            <p className="font-medium text-gray-900">{complaint.customer_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Contact Number</p>
            <div className="flex items-center space-x-2">
              <p className="font-medium text-gray-900">{complaint.contact_number || 'N/A'}</p>
              {complaint.contact_number && (
                <>
                  <button
                    onClick={() => copyToClipboard(complaint.contact_number, 'Phone number')}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <Copy size={16} />
                  </button>
                  <a href={`tel:${complaint.contact_number}`} className="p-1 hover:bg-gray-100 rounded">
                    <Phone size={16} />
                  </a>
                </>
              )}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Alternate Number</p>
            <p className="font-medium text-gray-900">{complaint.alternate_number || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Vehicle Number</p>
            <p className="font-medium text-gray-900">{complaint.vehicle_number || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Location Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <MapPin size={20} className="mr-2" />
          Location Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Address</p>
            <p className="font-medium text-gray-900">{complaint.address || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Pincode</p>
            <p className="font-medium text-gray-900">{complaint.pincode || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Location</p>
            <p className="font-medium text-gray-900">{complaint.location || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Service Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Wrench size={20} className="mr-2" />
          Service Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Service Type</p>
            <p className="font-medium text-gray-900">{complaint.service_type || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Problem Description</p>
            <p className="font-medium text-gray-900">{complaint.problem || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Remarks</p>
            <p className="font-medium text-gray-900">{complaint.remark || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Assignment Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Assignment Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Service Advisor</p>
            <p className="font-medium text-gray-900">{complaint.assigned_advisor_name || 'Unassigned'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Mechanic</p>
            <div className="flex items-center space-x-2">
              <p className="font-medium text-gray-900">{complaint.assigned_mechanic_name || 'Unassigned'}</p>
              {complaint.assigned_mechanic_contact && (
                <a href={`tel:${complaint.assigned_mechanic_contact}`} className="p-1 hover:bg-gray-100 rounded">
                  <Phone size={16} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <DollarSign size={20} className="mr-2" />
          Payment Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Customer Quoted</p>
            <p className="font-medium text-gray-900">
              ₹{Number(complaint.customer_quoted_amount || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Mechanic Payment</p>
            <p className="font-medium text-gray-900">
              ₹{Number(complaint.payment_mechanic || 0).toLocaleString('en-IN')}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Payment Received</p>
            <p className="font-medium text-gray-900">
              ₹{Number(complaint.payment_rcv || 0).toLocaleString('en-IN')}
            </p>
          </div>
        </div>
      </div>

      {/* Timeline */}
      {timeline && timeline.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Clock size={20} className="mr-2" />
            Status Timeline
          </h2>
          <div className="space-y-4">
            {timeline.map((event, idx) => (
              <div key={event.id || idx} className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{event.status}</p>
                  <p className="text-sm text-gray-600">
                    {formatDateTime(event.changed_at)}
                    {event.changed_by_name && ` • ${event.changed_by_name}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Admin Notes</h2>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 mb-4"
              placeholder="Add admin notes..."
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                {savingNotes ? 'Saving...' : 'Save Notes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintDetails;

